import React, { useMemo, useState } from 'react';
import { MapPin, GraduationCap } from 'lucide-react';
import FlagIssueModal from './FlagIssueModal';
import ParentSignalsBlock from './ParentSignalsBlock';
import SchoolHighlights from './SchoolHighlights';
import {
  projectFees, formatRM,
  displayName, shortDisplayName, friendlySchoolLocation,
  buildRevealedCardReasoning, ageAppropriateCurriculum, curriculumFamilyShort,
} from '../../lib/reportUtils';

/**
 * Revealed school card (shown after unlock) for #2, #3, and near-misses.
 * Includes full name, key stats, fee projection TABLE, and a plain-English
 * reasoning paragraph honestly referencing the parent's priorities + location.
 */
const RevealedSchoolCard = ({ school, rank, currentGrade, neutralReason, reportId, apiUrl, summary, isNearMiss = false, highlightsTier = 'full' }) => {
  const [flagOpen, setFlagOpen] = useState(false);
  const annualFees = school.annual_fees || {};

  const fromYear = Math.max(1, currentGrade || 1);
  const toYear = 11;

  const rows = useMemo(
    () => (isNearMiss ? [] : projectFees(annualFees, fromYear, toYear)),
    [annualFees, fromYear, toYear, isNearMiss]
  );

  const reasoning = useMemo(
    () => (isNearMiss ? null : buildRevealedCardReasoning(summary, school, rows, rank || 2)),
    [summary, school, rows, rank, isNearMiss]
  );

  // Near-miss one-liner: derive from match_percentage / fee_difference
  const nearMissOneLiner = useMemo(() => {
    if (!isNearMiss) return null;
    if (typeof school.fee_difference === 'number' && school.fee_difference > 0) {
      return 'Strong school — fees fall outside your range.';
    }
    return 'Came close — fewer of your top priorities were met here.';
  }, [isNearMiss, school]);

  const locationLabel = friendlySchoolLocation(school);
  const fullName = displayName(school);

  return (
    <div
      className="rounded-xl bg-white border border-[#E8DED3] p-5 shadow-[0_2px_10px_rgba(44,40,35,0.04)]"
      data-testid={`revealed-school-card-${school.id || rank || 'x'}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          {rank && (
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#E85D35] mb-1">
              Match #{rank}
            </p>
          )}
          <h3 className="font-black text-lg text-[#2C2823] leading-snug" data-testid="revealed-school-name">
            {fullName}
          </h3>
        </div>
        {school.match_percentage != null && (
          <span className="shrink-0 inline-flex items-center px-2.5 py-1 rounded-full bg-[#2C2823] text-white text-xs font-bold">
            {school.match_percentage}%
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#6B6560] mb-3">
        {locationLabel && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {locationLabel}
          </span>
        )}
        {school.curriculum_framework && (
          <span className="inline-flex items-center gap-1">
            <GraduationCap className="w-3.5 h-3.5" />
            {isNearMiss
              ? curriculumFamilyShort(school.curriculum_framework)
              : ageAppropriateCurriculum(school, currentGrade)}
          </span>
        )}
      </div>

      {isNearMiss && nearMissOneLiner && (
        <p
          className="text-sm text-[#2C2823] leading-relaxed bg-[#FFF5EB] border border-[#F0E5D7] rounded-lg p-3"
          data-testid={`near-miss-one-liner-${school.id || 'x'}`}
        >
          {nearMissOneLiner}
        </p>
      )}

      {!isNearMiss && neutralReason && (
        <p className="text-xs text-[#9B8F88] italic mb-3">{neutralReason}</p>
      )}

      {!isNearMiss && reasoning && (
        <p
          className="text-sm text-[#2C2823] leading-relaxed bg-[#F0F9F4] border border-emerald-100 rounded-lg p-3 mb-3"
          data-testid={`revealed-reasoning-${school.id || rank || 'x'}`}
        >
          {reasoning}
        </p>
      )}

      {/* Fee projection table — hidden for near-miss (compact) variant */}
      {!isNearMiss && (rows.some((r) => r.fee != null && r.fee > 0) ? (
        <div className="mt-3">
          <p className="text-xs text-[#9B8F88] mb-1.5 uppercase tracking-wider font-semibold">
            Annual fees — Year {fromYear} to Year {toYear} (3% escalation)
          </p>
          <div className="rounded-lg border border-[#E8DED3] overflow-hidden">
            <table className="w-full text-sm" data-testid={`revealed-fee-table-${school.id || rank || 'x'}`}>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.year} className="odd:bg-white even:bg-[#FFF9F2] border-b last:border-b-0 border-[#E8DED3]">
                    <td className="px-3 py-1.5 font-semibold text-[#2C2823] w-1/3">Year {r.year}</td>
                    <td className="px-3 py-1.5 text-[#2C2823] text-right">
                      {r.fee != null && r.fee > 0 ? (
                        <>
                          {formatRM(r.fee)}
                          {r.projected && <span className="text-[#9B8F88] font-normal"> · projected</span>}
                        </>
                      ) : (
                        <span className="text-[#9B8F88]">Fee data temporarily unavailable</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-xs text-[#9B8F88] italic mt-3">
          Fee data temporarily unavailable for {shortDisplayName(school)}.
        </p>
      ))}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => setFlagOpen(true)}
          className="text-[11px] font-semibold text-[#9B8F88] hover:text-[#E85D35] underline underline-offset-4 decoration-dotted"
          data-testid={`flag-issue-link-${school.id || 'revealed'}`}
        >
          Notice something wrong?
        </button>
      </div>

      {!isNearMiss && <SchoolHighlights school={school} tier={highlightsTier} compact />}

      <ParentSignalsBlock
        schoolId={school.id}
        schoolName={shortDisplayName(school)}
        apiUrl={apiUrl}
        compact
      />

      <FlagIssueModal
        open={flagOpen}
        onClose={() => setFlagOpen(false)}
        school={school}
        reportId={reportId}
        apiUrl={apiUrl}
      />
    </div>
  );
};

export default RevealedSchoolCard;
